<template>
    <!-- App.vue -->
    <v-app>
        <!-- Navigation Drawer -->
        <base-app-navigation
            v-if="isAuthed"
            :drawer-width="340"
            :items="navigationFiltered"
        ></base-app-navigation>

        <!--  App Bar -->
        <base-app-bar
            v-if="isAuthed"
            :email-address="emailAddress"
            :title="title"
        >
            <v-spacer></v-spacer>
            <!-- Search Input -->
            <search-input
                :items="globalSearchHandler.data"
                :loading="globalSearchHandler.loading"
                @clear="resetGlobalSearch"
                @search="handleGlobalSearch"
            ></search-input>
            <!-- Account Menu -->
            <account-menu
                :email-address="emailAddress"
                :help-url="windsorUrls.CONTACT_US_URL"
                :status-page-url="windsorUrls.STATUS_PAGE_URL"
                account-redirect-url="/api/auth/my-account"
                @click:logout="handleLogout"
            ></account-menu>
        </base-app-bar>

        <!-- Confirm Dialog -->
        <base-confirm-dialog
            v-bind="confirmProps"
            @close="closeConfirm"
            @submit="submitConfirm"
        ></base-confirm-dialog>

        <!-- Content Container -->
        <v-main>
            <v-container :class="{'fill-height': !isAuthed}" fluid>
                <base-force-mfa-error
                    v-if="hasMfaError"
                    :error="mfaError"
                    redirect-url="/api/auth/my-2fa"
                ></base-force-mfa-error>
                <router-view v-show="!hasMfaError"></router-view>
            </v-container>
        </v-main>

        <!-- App Footer -->
        <base-app-footer v-if="isAuthed" id="footer" :title="footer" :version="version"></base-app-footer>
    </v-app>
</template>

<style>
    .no-wrap,
    .v-data-table-header th {
        white-space: nowrap;
    }

    .fixed-table .v-data-table__wrapper {
        max-height: calc(100vh - 240px);
    }

    .v-chip--select {
        margin-top: 6px !important;
    }

    .search-row {
        cursor: pointer;
    }

    .search-row.active {
        background: #d3e8d5 !important;
    }
</style>

<script>
    import {mapActions, mapGetters} from 'vuex'
    import {LOGOUT} from './router/route-names'
    import {filter, includes, isEmpty, map} from 'lodash'
    import windsorUrls from '@framework/store/static/windsor-urls'
    import navigation from '@/router/navigation'
    // Utils
    import logger from '@core/utils/logger'
    // Mixins
    import BaseValidationErrorsMixin from '@framework/mixins/base-validation-errors.mixin'
    import GlobalSearchMixin from '@/mixins/search/global-search.mixin'
    // Components
    import AccountMenu from '@/components/AccountMenu'
    import BaseAppBar from '@framework/components/BaseAppBar'
    import BaseAppFooter from '@framework/components/BaseAppFooter'
    import BaseAppNavigation from '@framework/components/BaseAppNavigation'
    import BaseForceMfaError from '@framework/components/BaseForceMfaError'
    import SearchInput from '@/components/search/SearchInput'

    export default {
        name: 'App',
        metaInfo: {
            title: 'Loading WIMS',
            titleTemplate: '%s | WIMS Portal'
        },
        mixins: [
            BaseValidationErrorsMixin,
            GlobalSearchMixin
        ],
        data: () => ({
            loading: false,
            pingIntervalId: false,
            windsorUrls,
            version: process.env.VERSION,
            navigation
        }),
        components: {
            AccountMenu,
            BaseAppBar,
            BaseAppFooter,
            BaseAppNavigation,
            BaseForceMfaError,
            SearchInput
        },
        computed: {
            ...mapGetters('app', ['title', 'footer']),
            ...mapGetters('confirm', {confirmProps: 'props'}),
            ...mapGetters('auth', [
                'isAuthed',
                'fromRoute',
                'nextRoute',
                'token',
                'emailAddress',
                'mfaError',
                'hasMfaError',
                'hasSomePermissions'
            ]),
            navigationFiltered () {
                const vm = this
                const items = map(navigation, (item) => {
                    let children = item?.children

                    if (!children) {
                        return item
                    }

                    return {
                        ...item,
                        children: filter(children, child => child?.permissions
                            ? vm.hasSomePermissions(child?.permissions)
                            : true
                        )
                    }
                })

                return filter(items, item => {
                    if (item?.children && isEmpty(item.children)) {
                        return false
                    }

                    return item?.permissions
                        ? vm.hasSomePermissions(item?.permissions)
                        : true
                })
            }
        },
        methods: {
            ...mapActions('auth', ['ping', 'me']),
            ...mapActions('confirm', ['closeConfirm', 'submitConfirm']),
            handleLogout () {
                this.$router.push({name: LOGOUT})
            }
        },
        watch: {
            isAuthed (newVal, oldVal) {
                const vm = this

                if ((newVal && !oldVal) && !vm.pingIntervalId) {
                    vm.pingIntervalId = setInterval(async () => {
                        try {
                            await vm.ping()
                        } catch (e) {
                            if (e && e.status === 401) {
                                vm.handleLogout()
                                clearInterval(vm.pingIntervalId)
                            } else {
                                const message = 'Ping Error'
                                logger.error('[APP]', message, e)
                                if (vm.$sentry) {
                                    vm.$sentry.withScope(scope => {
                                        scope.setExtra('context', e)
                                        vm.$sentry.captureMessage(message)
                                    })
                                }
                            }
                        }
                    }, 30000)
                }
            }
        },
        created () {
            const vm = this
            if (!includes(['/login', '/auth-check'], vm.$route.path)) {
                vm.$router.push({path: `/login${window.location.search}`})
            }
        }
    }
</script>
